import React, { useState, useContext, useEffect } from 'react';
import PropTypes from 'prop-types';
import { observer } from 'mobx-react';
import { getIn, useFormikContext } from 'formik';
import { useStore } from 'core/store/store.mobx';
import { AuthContext } from 'core/components/auth';

import Section from 'core/components/section';
import RepeatField from 'core/components/repeat-field';

function People({
  person: Person,
  addForm: AddForm,
  addLabel,
  title,
  disabled,
  id,
  singular,
  plural,
  children,
  type,
  classes,
  hasAuto,
  fromPolicy,
  ...rest
}) {
  const [folded, setFolded] = useState([]);
  const { values, setFieldValue, setFieldTouched, initialValues, touched } = useFormikContext();
  const list = getIn(values, id) ?? [];

  const personsList = values.drivers ?? values.people;
  // if there is a second person on a Home policy (id === 'people'), they are a coapplicant
  // however, if the second person is newly added they will not have isCoapplicant === true yet -> need to 'save' the change before being able to swap
  const showApplicantSwapButton =
    id === 'people' ? personsList?.length > 1 : personsList?.some((person) => person.isCoApplicant === true);

  const toggleFold = (i, foldedStatus) =>
    setFolded((prevFolded) => [...prevFolded.slice(0, i), foldedStatus, ...prevFolded.slice(i + 1, prevFolded.length)]);

  const {
    offer: offerStore,
    account: {
      policies: { policy: policyStore }
    }
  } = useStore();

  const session = useContext(AuthContext);

  const allowLicensedActions = fromPolicy
    ? policyStore?.getAllowLicensedActions(session)
    : offerStore?.getIsLicensedForState(session);

  const [disableSwapButton, setDisableSwapButton] = useState(true);
  const [disableNameChange, setDisableNameChange] = useState(false);

  useEffect(() => {
    let someNameChange = false;
    if (personsList) {
      for (let i = 0; i < personsList.length; i++) {
        someNameChange =
          (getIn(touched, `${id}[${i}].firstName`) || getIn(touched, `${id}[${i}].lastName`)) ?? someNameChange;
      }
    }
    const initialPersons = initialValues.drivers ?? initialValues.people;

    const coApplicant = list.find((person) => person.isCoApplicant === true);
    const initialVersionOfCurrentCoapp = initialPersons?.find((person) => person.id === coApplicant?.id);

    const coApplicantIsNewlyCoapplicant = !(
      (initialVersionOfCurrentCoapp?.isCoApplicant || initialVersionOfCurrentCoapp?.isPrimary) &&
      coApplicant?.isCoApplicant
    );

    setDisableSwapButton(!allowLicensedActions || !!coApplicantIsNewlyCoapplicant || someNameChange);
  }, [list, allowLicensedActions]);

  const pushItem = (val) => {
    const item = val;

    Object.keys(item).forEach((key) => {
      if (item[key] === '') {
        item[key] = null;
      }
    });
    setFieldValue(id, [...list, item]);
    setFieldTouched(id);
  };

  const removeItem = (i) => {
    const newList = list.slice();
    newList.splice(i, 1);
    setFieldValue(id, newList);
    setFieldTouched(id);
  };

  const swapPrimaryApplicant = () => {
    if (!fromPolicy) {
      const newList = list.map((item) => {
        const { isPrimary, isCoApplicant } = item;

        const isApplicant = isPrimary || isCoApplicant;

        if (isCoApplicant) {
          setFieldValue('fname', item.firstName);
          setFieldValue('lname', item.lastName);
          setFieldTouched('fname');
          setFieldTouched('lname');
        }

        return {
          ...item,
          isPrimary: isApplicant && !isPrimary,
          isCoApplicant: isApplicant && !isCoApplicant
        };
      });

      setFieldValue(id, newList);
      setFieldTouched(id);
    } else {
      // post-bind swaps
      // (note: the policy values do not include fname and lname)
      const newList = list.map((item) => {
        const { isPrimary, isCoApplicant } = item;
        const isApplicant = isPrimary || isCoApplicant;

        return {
          ...item,
          isPrimary: isApplicant && !isPrimary,
          isCoApplicant: isApplicant && !isCoApplicant
        };
      });

      setDisableNameChange(true);
      setFieldValue(id, newList);
      setFieldTouched(id);
    }
  };

  return (
    <Section
      classes={classes}
      title={title}
      type={type || 'SubSection'}
      rightLabel="Total"
      rightValue={`${list.length} ${list.size === 1 ? singular : plural}`}
    >
      {children}
      <RepeatField
        initialLength={list.length}
        key={list.length}
        maxSize={40}
        control={() => (
          <AddForm
            id={id}
            disabled={disabled}
            onAdd={pushItem}
            label={addLabel}
            currentPeopleOrDrivers={values.drivers || values.people}
          />
        )}
      >
        {({ onRemove, focus }, i) => {
          const p = list[i];
          return (
            <Person
              fieldName={`${id}[${i}]`}
              fieldId={id}
              key={p.id || i}
              index={i}
              item={p}
              onRemove={removeItem}
              removeFromList={onRemove}
              focus={focus}
              initialFold={!!folded[i]}
              onFold={toggleFold}
              disabled={disabled}
              previous={list[i - 1]}
              count={list.length}
              swapPrimaryApplicant={swapPrimaryApplicant}
              showApplicantSwapButton={showApplicantSwapButton}
              disableSwapButton={disableSwapButton}
              disableNameChange={disableNameChange}
              allowLicensedActions={allowLicensedActions}
              hasAuto={hasAuto}
              fromPolicy={fromPolicy}
              {...rest}
            />
          );
        }}
      </RepeatField>
    </Section>
  );
}

People.propTypes = {
  id: PropTypes.string.isRequired,
  title: PropTypes.string,
  person: PropTypes.oneOfType([PropTypes.func, PropTypes.object]).isRequired,
  addForm: PropTypes.oneOfType([PropTypes.func, PropTypes.object]).isRequired,
  addLabel: PropTypes.string,
  disabled: PropTypes.bool,
  singular: PropTypes.string,
  plural: PropTypes.string,
  children: PropTypes.node,
  type: PropTypes.string,
  classes: PropTypes.object,
  hasAuto: PropTypes.bool
};

People.defaultProps = {
  title: 'People',
  disabled: false,
  singular: 'person',
  plural: 'people',
  addLabel: undefined,
  children: undefined,
  classes: {},
  type: 'SubSection',
  hasAuto: false
};

export default observer(People);
